import {
    BlockOutlined,
    CheckCircleOutline,
    CreditCardOffOutlined,
    CreditCardOutlined,
    ReportProblemOutlined,
} from '@mui/icons-material';
import {Box, Button, Typography} from '@mui/material';
import {FC, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {usersApi} from 'src/api';
import {tagsApi} from 'src/api/tags';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import AMPModal from 'src/components/common/AMPModal';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {CancelButton} from 'src/components/common/CancelButton';
import {StatsStrip} from 'src/components/common/StatsStrip';
import {StatsStripTile} from 'src/components/common/StatsStrip/StatsStrip.types';
import {useCan} from 'src/permissions/hooks';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {FeedbackTypeEnum} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {confirmActionsSx, confirmBodySx, confirmButtonSx} from './RfidHolderStrip.sx';
import {RfidHolderStripProps} from './RfidHolderStrip.types';

type HolderAction = 'lost' | 'deactivate';

/**
 * The strip a card's holder opens the RFID page with - the same block the back office gets, with the
 * holder's own tiles.
 *
 * Two count how many of their cards work and how many are switched off, and filter the list like any
 * strip's tiles. Two do something: report a card lost - a message to support, under
 * profile.sendFeedback - and switch one off, under rfidTags.deactivate, at every charger at once. A lost
 * card wants both; one somebody simply stopped using wants only the second. Neither switches a card
 * back on: that stays with whoever holds rfidTags.update.
 */
export const RfidHolderStrip: FC<RfidHolderStripProps> = ({
                                                              stats,
                                                              businessId,
                                                              companyId,
                                                              employeeId,
                                                              activeStatus,
                                                              onSelectStatus,
                                                          }) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const {authInfo} = useAppSelector((state) => state.common);
    const canReport = useCan('profile.sendFeedback');
    const canDeactivate = useCan('rfidTags.deactivate');
    const [sendFeedback] = usersApi.usePostFeedbackMutation();
    const [deactivateTag] = tagsApi.useDeactivateTagMutation();
    const [action, setAction] = useState<HolderAction>('lost');
    const [open, setOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<number>(0);

    // The holder's cards whatever the list below is filtered to: switching one off has to work while
    // the list shows only the switched-off ones. A holder has a handful, so one page is all of them.
    const {data: holderTags} = tagsApi.useGetTagsQuery({
        params: {page: 0, size: 100},
        data: {
            businessId,
            companyId,
            employeeId,
            keyword: '',
            sortAsc: false,
            sortField: 'CREATED_AT',
        },
    });

    const loaded = Boolean(holderTags);
    const tags = holderTags?.content ?? [];
    const activeTags = tags.filter((tag) => !tag.blocked);
    const isDeactivate = action === 'deactivate';
    const candidates = isDeactivate ? activeTags : tags;
    const selected = candidates.find((tag) => tag.id === selectedId) ?? candidates[0];

    const ask = (next: HolderAction) => () => {
        const pool = next === 'deactivate' ? activeTags : tags;
        setAction(next);
        setSelectedId(pool[0]?.id ?? 0);
        setOpen(true);
    };

    const close = () => setOpen(false);

    const run = async (request: () => Promise<unknown>, notice: string) => {
        try {
            dispatch(preloaderChangeStatus(true));
            await request();
            NotifySuccess(notice);
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message ?? error.data?.error);
            }
        } finally {
            dispatch(preloaderChangeStatus(false));
            close();
        }
    };

    const reportLost = () => selected && run(
        () => sendFeedback({
            userId: authInfo.id,
            data: {
                feedbackType: FeedbackTypeEnum.HELP,
                // Support reads this without the page in front of them: which card, and for which car.
                message: [
                    intl.formatMessage({id: 'rfidLostBadgeMessage'}, {idTag: selected.idTag}),
                    selected.licensePlate
                        ? `${intl.formatMessage({id: 'licensePlate'})}: ${selected.licensePlate}`
                        : '',
                ].filter(Boolean).join('. '),
            },
        }).unwrap(),
        intl.formatMessage({id: 'messageSent'}),
    );

    const deactivate = () => selected && run(
        () => deactivateTag({
            tagId: selected.id,
            params: {businessId},
        }).unwrap(),
        intl.formatMessage({id: 'rfidDeactivatedNotice'}, {idTag: selected.idTag}),
    );

    const tiles: StatsStripTile[] = [
        {
            key: 'active',
            label: 'active',
            value: stats ? stats.tags - stats.blocked : undefined,
            icon: <CheckCircleOutline/>,
            tone: 'success',
            active: activeStatus === 'active',
            onClick: () => onSelectStatus('active'),
        },
        {
            key: 'deactivated',
            label: 'rfidDeactivated',
            value: stats?.blocked,
            icon: <CreditCardOffOutlined/>,
            tone: 'neutral',
            active: activeStatus === 'deactivated',
            onClick: () => onSelectStatus('deactivated'),
        },
    ];

    // An action with nothing to act on says so and stops being a button, rather than opening an empty
    // dialog. A card already switched off can still be reported lost.
    if (canReport) {
        tiles.push({
            key: 'lost',
            label: 'rfidLostBadge',
            description: !loaded || tags.length ? 'rfidLostBadgeHint' : 'rfidNoTags',
            icon: <ReportProblemOutlined/>,
            tone: 'warning',
            onClick: tags.length ? ask('lost') : undefined,
        });
    }

    if (canDeactivate) {
        tiles.push({
            key: 'deactivate',
            label: 'rfidDeactivateTag',
            description: !loaded || activeTags.length ? 'rfidDeactivateHint' : 'rfidNoActiveTag',
            icon: <BlockOutlined/>,
            tone: 'danger',
            onClick: activeTags.length ? ask('deactivate') : undefined,
        });
    }

    return (
        <>
            <StatsStrip
                icon={<CreditCardOutlined/>}
                title="rfidPageHeader"
                subtitle={intl.formatMessage({id: 'tagStatsSubtitle'}, {tags: stats?.tags ?? 0})}
                tiles={tiles}
            />

            <AMPModal
                open={open}
                handleClose={close}
                title={intl.formatMessage({id: isDeactivate ? 'rfidDeactivateTag' : 'rfidLostBadge'})}
                type="sm"
                fullWidth
                withoutPaddings
            >
                <Box sx={confirmBodySx}>
                    {/* Only a choice when there is one to make: with a single card it is simply that card. */}
                    {candidates.length > 1 && (
                        <AMPFormSelect
                            label="rfidTag"
                            input="rfidTag"
                            value={selected?.id ?? ''}
                            data={candidates.map((tag) => ({
                                id: tag.id,
                                name: tag.licensePlate ? `${tag.idTag} · ${tag.licensePlate}` : tag.idTag,
                                value: tag.id,
                            }))}
                            onChange={(event) => setSelectedId(Number(event.target.value))}
                        />
                    )}
                    <Typography>
                        <FormattedMessage
                            id={isDeactivate ? 'rfidDeactivateConfirm' : 'rfidLostBadgeConfirm'}
                            values={{idTag: selected?.idTag ?? ''}}
                        />
                    </Typography>
                </Box>
                <Box sx={confirmActionsSx}>
                    <CancelButton
                        text="cancel"
                        handler={close}
                    />
                    <Button
                        variant="contained"
                        color={isDeactivate ? 'error' : 'primary'}
                        sx={confirmButtonSx}
                        disabled={!selected}
                        onClick={isDeactivate ? deactivate : reportLost}
                    >
                        <Typography variant="button">
                            <FormattedMessage id={isDeactivate ? 'rfidDeactivate' : 'send'}/>
                        </Typography>
                    </Button>
                </Box>
            </AMPModal>
        </>
    );
};
